import { Link } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import {
  Menu,
  X,
  ShoppingCart,
} from "lucide-react";

import PolyboinicLogo from "../../src/image/Polyboinic-Technologies-Logo.png";
import { CartContext } from "../context/CartContext";
import { AuthContext } from "../context/AuthContext";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  const { cart } = useContext(CartContext);
  const { user, isAuthenticated } = useContext(AuthContext);

  const cartCount = cart.reduce(
    (total, item) => total + (item.quantity || 1),
    0
  );

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // ================================
  // NAVIGATION MENU
  // ================================
  const menuItems = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about-us" },
    { name: "Solutions", path: "/solutions" },
    { name: "Our Products", path: "/our-fruits-and-vegetables" },
    { name: "Technology", path: "/technology" },
    { name: "Projects", path: "/projects" },
    { name: "Blog", path: "/blog" },
    { name: "Contact", path: "/contact" },
  ];

  const dashboardPath =
    user?.role === "admin"
      ? "/dashboard"
      : "/buyer";

  return (
    <header
      className={`w-full transition-all duration-500 z-50 ${
        scrolled
          ? "fixed top-0 left-0 bg-white shadow-lg py-3"
          : " top-0 left-0 bg-white py-3"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-5 lg:px-8">
        <div className="flex items-center justify-between">

          {/* =================================
              LOGO
          ================================= */}
          <Link to="/">
            <img
              src={PolyboinicLogo}
              alt="Polybionic"
              className={`transition-all duration-500 ${
                scrolled
                  ? "h-12 md:h-14"
                  : "h-20 md:h-20"
              }`}
            />
          </Link>
          {/* =================================
              DESKTOP MENU
          ================================= */}
          <nav className="hidden lg:flex items-center gap-7 xl:gap-8">

            {menuItems.map((item) => {
              const isActive =
                window.location.pathname === item.path;

              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`relative whitespace-nowrap py-5 text-[13px] font-semibold transition-all duration-300 ${
                    scrolled
                      ? isActive
                        ? "text-[#087f4f]"
                        : "text-[#17231e] hover:text-[#087f4f]"
                      : isActive
                      ? "text-[#087f4f]"
                      : "text-[#17231e] hover:text-[#087f4f]"
                  }`}
                >
                  {item.name}

                  {/* Active underline */}
                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 h-[3px] w-7 -translate-x-1/2 rounded-full bg-[#087f4f]" />
                  )}
                </Link>
              );
            })}

          </nav>
          {/* =================================
              DESKTOP RIGHT SECTION
          ================================= */}
          <div className="hidden lg:flex items-center gap-5">

            {/* Search */}
            <button
              className={`flex h-9 w-9 items-center justify-center rounded-full border transition ${
                scrolled
                  ? "border-gray-200 text-gray-700 hover:border-green-600 hover:text-green-600"
                  : "border-gray-200 text-gray-700 hover:border-green-600 hover:text-green-600"
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </button>


            {/* Cart */}
            <Link
              to="/cart"
              className="relative"
            >
              <ShoppingCart
                size={23}
                className="text-gray-800"
              />

              {cartCount > 0 && (
                <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                  {cartCount}
                </span>
              )}
            </Link>


            {/* Login / Dashboard */}
            <Link
              to={
                isAuthenticated
                  ? dashboardPath
                  : "/login"
              }
              className="text-[13px] font-semibold text-gray-800 transition hover:text-green-600"
            >
              {isAuthenticated
                ? "Dashboard"
                : "Login"}
            </Link>


            {/* Get A Quote */}
            <Link
              to="/join-us"
              className="rounded-full bg-[#087f4f] px-5 py-2.5 text-[13px] font-bold text-white transition-all duration-300 hover:bg-[#056b42]"
            >
              Get a Quote →
            </Link>

          </div>


          {/* =================================
              MOBILE RIGHT SECTION
          ================================= */}
          <div className="flex items-center gap-4 lg:hidden">

            {/* Cart */}
            <Link
              to="/cart"
              className="relative"
            >
              <ShoppingCart
                size={25}
                className="text-gray-800"
              />

              {cartCount > 0 && (
                <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                  {cartCount}
                </span>
              )}
            </Link>


            {/* Menu Button */}
            <button
              onClick={() =>
                setMobileMenu(!mobileMenu)
              }
              className="text-gray-800"
              aria-label="Toggle menu"
            >
              {mobileMenu ? (
                <X size={28} />
              ) : (
                <Menu size={28} />
              )}
            </button>

          </div>

        </div>
      </div>


      {/* =================================
          MOBILE MENU
      ================================= */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          mobileMenu
            ? "max-h-[700px]"
            : "max-h-0"
        }`}
      >
        <div className="border-t border-gray-100 bg-white px-6 py-4 shadow-xl">

          <nav className="flex flex-col">

            {menuItems.map((item) => {
              const isActive =
                window.location.pathname === item.path;

              return (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() =>
                    setMobileMenu(false)
                  }
                  className={`border-b border-gray-100 py-3.5 text-sm font-semibold transition ${
                    isActive
                      ? "text-[#087f4f]"
                      : "text-gray-800 hover:text-[#087f4f]"
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}


            {/* Login / Dashboard */}
            <Link
              to={
                isAuthenticated
                  ? dashboardPath
                  : "/login"
              }
              onClick={() =>
                setMobileMenu(false)
              }
              className="border-b border-gray-100 py-3.5 text-sm font-semibold text-gray-800 hover:text-[#087f4f]"
            >
              {isAuthenticated
                ? "Dashboard"
                : "Login"}
            </Link>


            {/* Get Quote */}
            <Link
              to="/join-us"
              onClick={() =>
                setMobileMenu(false)
              }
              className="mt-4 rounded-full bg-[#087f4f] px-6 py-3 text-center text-sm font-bold text-white transition hover:bg-[#056b42]"
            >
              Get a Quote →
            </Link>

          </nav>

        </div>
      </div>
    </header>
  );
}